/**
 * HouseUnlockDialog — prompts for a house PIN and stores the unlock
 * in sessionStorage on success.
 *
 * Usage:
 *   <HouseUnlockDialog
 *     houseId={h.id}
 *     houseName={h.name}
 *     onUnlocked={() => ...}
 *     onClose={() => ...}
 *   />
 *
 * The dialog calls `/api/houses/{id}/verify-pin` to check the PIN
 * server-side (rate-limited) before caching it on the device.
 */
import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { setHouseUnlock } from "@/lib/houseLock";
import { toast } from "sonner";
import { Lock, X } from "lucide-react";

export default function HouseUnlockDialog({ houseId, houseName, reason, onUnlocked, onClose }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin.trim()) {
      toast.error("Enter the house PIN");
      return;
    }
    setBusy(true);
    try {
      const res = await api.verifyHousePin(houseId, pin.trim());
      if (!res?.ok) {
        toast.error("Wrong house PIN");
        return;
      }
      setHouseUnlock(houseId, pin.trim());
      toast.success(`${houseName || "House"} unlocked`);
      onUnlocked && onUnlocked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="house-unlock-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[color:var(--bg)] md:rounded-[18px] rounded-t-[24px] w-full md:max-w-sm p-5 md:p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--bg-soft)] flex items-center justify-center flex-shrink-0">
            <Lock className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <div className="label-tiny">House locked</div>
            <h2 className="font-display-italic text-2xl leading-tight mt-0.5">
              {houseName || "This house"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            aria-label="Close"
            data-testid="house-unlock-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        <p className="text-[14px] leading-snug text-[color:var(--ink-2)]">
          {reason ||
            `Enter the PIN for ${houseName || "this house"} to see and change its members, check-ins and events on this device.`}
        </p>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          autoComplete="off"
          placeholder="House PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 12))}
          className="ff-input text-center text-2xl tabular"
          data-testid="house-unlock-pin-input"
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="ff-btn"
            data-testid="house-unlock-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="ff-btn ff-btn-primary"
            data-testid="house-unlock-submit"
          >
            {busy ? "Unlocking…" : "Unlock"}
          </button>
        </div>
      </form>
    </div>
  );
}
